import React, { useState } from 'react';
import { Send, CheckCircle, Mail, Building2, User, AtSign, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../api';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in all fields.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      await api.submitContact(form);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const videoBackground = (
    <>
      <video
        className="contact-bg-video"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
      >
        <source
          src="https://lofimusic.sgp1.digitaloceanspaces.com/videos/hero-contact.mp4"
          type="video/mp4"
        />
      </video>
      <div className="contact-bg-overlay" />
    </>
  );

  if (success) {
    return (
      <div className="ct-page" data-testid="contact-page">
        {videoBackground}
        <div className="ct-center">
          <motion.div
            className="ct-glass-card"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="ct-success" data-testid="contact-success">
              <div className="ct-success-icon">
                <CheckCircle size={40} />
              </div>
              <h2>Message Sent!</h2>
              <p>Thank you for reaching out. We'll get back to you as soon as possible.</p>
              <button
                className="ct-btn-outline"
                onClick={() => {
                  setSuccess(false);
                  setForm({ name: '', email: '', message: '' });
                }}
                data-testid="send-another-btn"
              >
                Send Another Message
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="ct-page" data-testid="contact-page">
      {videoBackground}
      <div className="ct-center">
        <motion.div
          className="ct-glass-card"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="ct-card-header">
            <h1>Get in Touch</h1>
            <p>
              Have a question, want to submit a demo, or interested in joining our roster?
            </p>
          </div>

          {/* Divider */}
          <div className="ct-divider" />

          {/* Form */}
          <form className="ct-form" onSubmit={handleSubmit} data-testid="contact-form">
            {error && (
              <div className="ct-error" data-testid="form-error">{error}</div>
            )}

            {/* Name + Email side by side */}
            <div className="ct-row">
              <div className="ct-field">
                <label htmlFor="name">Name</label>
                <div className="ct-input-wrap">
                  <User size={16} className="ct-input-icon" />
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    data-testid="contact-name"
                  />
                </div>
              </div>
              <div className="ct-field">
                <label htmlFor="email">Email</label>
                <div className="ct-input-wrap">
                  <AtSign size={16} className="ct-input-icon" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    data-testid="contact-email"
                  />
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="ct-field">
              <label htmlFor="message">Message</label>
              <div className="ct-input-wrap ct-textarea-wrap">
                <MessageSquare size={16} className="ct-input-icon ct-textarea-icon" />
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project, demo submission, or question..."
                  rows={5}
                  data-testid="contact-message"
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="ct-submit"
              disabled={loading}
              data-testid="contact-submit"
            >
              <span>{loading ? 'Sending...' : 'Send Message'}</span>
              <Send size={16} />
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
